import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { SearchParameters } from 'algoliasearch-helper';
import type { BaseHit } from 'instantsearch.js';
import shuffle from 'shuffle-array';

import type {
  BaseballPosition,
  NBAPosition,
  Position,
  Rarity,
  Tradeable,
  WalletStatus,
} from '#src/__generated__/globalTypes.ts';
import { Collection, Sport } from '#src/__generated__/globalTypes.ts';
import { FRONTEND_ASSET_HOST } from '#src/constants/assets.ts';
import type {
  AlgoliaCardIndexesName,
  AlgoliaCardIndexesNames,
  GallerySortName,
} from '#src/contexts/config/index.ts';

import type { Algolia_CardHit_anyCard } from './__generated__/algolia.graphql.ts';
import type { Scarcity } from './cards.ts';
import { OLD_US_SPORTS_SUPPLY, SUPPLY } from './cards.ts';
import { format as formatSeason } from './seasons.ts';

type AlgoliaSport = 'baseball' | 'football' | 'nba';

export const TOKENS_STACKED_LIMIT = 2;

export interface CardHit extends BaseHit {
  asset_id: string;
  slug: string;
  rarity: Scarcity;
  serial_number: number;
  season: string;
  tradeable_status: string;
  wallet_status: string;
  picture_url: string;
  video_urls?: {
    webm: string;
    mov: string;
  };
  player: {
    slug: string;
    display_name: string;
  };
  user: {
    slug: string;
  };
  team: {
    slug: string;
    long_name: string;
  };
  sport: AlgoliaSport;
  position: Position;
  __position: number;
  power: string;
  sale?: SaleHit;
}

export const AlgoliaSportsMappings: Readonly<{ [key in AlgoliaSport]: Sport }> =
  {
    nba: Sport.NBA,
    football: Sport.FOOTBALL,
    baseball: Sport.BASEBALL,
  };

const SportsMappings: Readonly<{ [key in Sport]: AlgoliaSport }> = {
  [Sport.NBA]: 'nba',
  [Sport.FOOTBALL]: 'football',
  [Sport.BASEBALL]: 'baseball',
};

const FakeCollectionMappings: Readonly<{ [key in AlgoliaSport]: Collection }> =
  {
    nba: Collection.NBA,
    baseball: Collection.BASEBALL,
    football: Collection.FOOTBALL,
  };

type SaleHit = {
  id: string;
  price: number;
};

export interface UserHit extends BaseHit {
  objectID: string;
  slug: string;
  id: string;
  profile: { picture: string; verified?: boolean };
  nickname: string;
  sorare_address_hex: string;
}

export interface ClubHit extends BaseHit {
  domestic_league: { display_name: string };
  objectID: string;
  pictureUrl: string;
  name: string;
  released: boolean;
  __position: number;
}

export interface NationalTeamHit extends BaseHit {
  objectID: string;
  display_name: string;
  name: string;
  country: { code: string };
}

export interface CountryHit extends BaseHit {
  objectID: string;
  code: string;
  name_en: string;
}

export interface PlayerHit extends BaseHit {
  objectID: string;
  avatarUrl: string;
  display_name: string;
  sport: AlgoliaSport;
  activeClub?: {
    name: string;
  };
}

export interface CompetitionHit extends BaseHit {
  objectID: string;
  display_name: string;
  display_name_de: string;
  display_name_en: string;
  display_name_es: string;
  display_name_fr: string;
  display_name_it: string;
  display_name_tr: string;
  display_name_ru: string;
  display_name_nl: string;
  display_name_pt: string;
  released: boolean;
  open_for_game_stats: boolean;
  code: string;
  pictureUrl: string;
}

export const convertUserHit = (
  hit: UserHit
): {
  __typename: 'User';
  slug: string;
  id: string;
  nickname: string;
  sorareAddress: string;
  suspended: boolean;
  pictureUrl: string;
  footballUserProfile: {
    __typename: 'FootballUserSportProfile';
    id: string;
    pictureFrame: {
      __typename: 'PictureFrameShopItem';
      id: string;
      name: string;
      pictureUrl: string;
    } | null;
  } | null;
  nbaUserProfile: {
    __typename: 'NBAUserSportProfile';
    id: string;
    pictureFrame: {
      __typename: 'PictureFrameShopItem';
      id: string;
      name: string;
      pictureUrl: string;
    } | null;
  } | null;
  baseballUserProfile: {
    __typename: 'BaseballUserSportProfile';
    id: string;
    pictureFrame: {
      __typename: 'PictureFrameShopItem';
      id: string;
      name: string;
      pictureUrl: string;
    } | null;
  } | null;
  profile: {
    __typename: 'UserProfile';
    id: string;
    badge: {
      __typename: 'UserProfileBadge';
      slug: string;
      pictureUrl: string;
      description: string;
    } | null;
  };
} => ({
  __typename: 'User',
  id: hit.id,
  slug: hit.objectID,
  nickname: hit.nickname,
  sorareAddress: hit.sorare_address_hex,
  suspended: false,
  pictureUrl: hit.profile?.picture,
  footballUserProfile: null,
  nbaUserProfile: null,
  baseballUserProfile: null,
  profile: {
    __typename: 'UserProfile',
    id: hit.id,
    badge: hit.profile.verified
      ? {
          __typename: 'UserProfileBadge',
          slug: 'verified',
          description: 'Verified',
          pictureUrl: `${FRONTEND_ASSET_HOST}/users/badges/verified.png`,
        }
      : null,
  },
});

export enum SaleType {
  SINGLE_SALE_OFFER = 'SingleSaleOffer',
  ENGLISH_AUCTION = 'EnglishAuction',
  PRIMARY_OFFER = 'PrimaryOffer',
}

export type MarketplaceHit = {
  objectID: string;
  sale?: { distinct_key: string | null; type: string | null };
  player?: { slug: string | null };
  rarity?: string | null;
  season?: number | null;
  slug: string;
  distinctKeyHitCount?: number;
};

export const convertToCardHit = (card: Algolia_CardHit_anyCard): CardHit => ({
  ...card,
  rarity: card.rarityTyped as Scarcity,
  serial_number: card.serialNumber,
  tradeable_status: card.tradeableStatus,
  wallet_status: card.walletStatus,
  picture_url: card.pictureUrl!,
  team: {
    slug: card.anyTeam.slug,
    long_name: card.anyTeam.name,
  },
  player: {
    slug: card.anyPlayer.slug,
    display_name: card.anyPlayer.displayName,
  },
  season: card.seasonYear.toString() || '',
  user: {
    slug: card.user?.slug || '',
  },
  sport: SportsMappings[card.sport],
  position: card.anyPositions[0],
  __position: 10,
  asset_id: card.assetId,
  power: '',
});

const convertCardHitToToken = (hit: CardHit) => ({
  ...hit,
  serialNumber: hit.serial_number,
  blockchainId: hit.blockchain_id,
  tradeableStatus: hit.tradeable_status as Tradeable,
  walletStatus: hit.wallet_status as WalletStatus,
  pictureUrl: hit.picture_url,
  borderRadius: 18,
  anyPositions: [hit.position],
  anyPlayer: {
    __typename: 'Player' as const,
    slug: hit.player.slug,
    displayName: hit.player.display_name,
  },
  anyTeam: {
    __typename: 'Club' as const,
    slug: hit.team.slug,
    name: hit.team.long_name,
  },
  rarityTyped: hit.rarity as Rarity,
  displayRarity: hit.rarity,
  seasonYear: Number(hit.season),
  singleCivilYear: hit.sport === 'baseball',
  sportSeason: {
    __typename: 'SportSeason' as const,
    name: formatSeason(Number(hit.season), {
      singleCivilYear: hit.sport === 'baseball',
    }),
  },
  // FIXME we'd need the supply to be indexed
  supply: (['nba', 'baseball'].includes(hit.sport) && hit.season < '2023'
    ? OLD_US_SPORTS_SUPPLY
    : SUPPLY)[hit.rarity as Rarity],
  user: hit.user
    ? {
        __typename: 'User' as const,
        slug: hit.user.slug,
      }
    : null,
  tokenOwner: hit.user
    ? {
        __typename: 'TokenOwner' as const,
        id: hit.user.slug,
        user: { __typename: 'User' as const, slug: hit.user.slug },
      }
    : null,
  sport: AlgoliaSportsMappings[hit.sport] || Sport.FOOTBALL,
  collection: FakeCollectionMappings[hit.sport] || Collection.FOOTBALL, // this assertion is factually wrong but that's a separate concern
});

const convertCardHitToTypename = (hit: CardHit) => {
  switch (hit.sport) {
    case 'baseball':
      return 'BaseballCard';
    case 'nba':
      return 'NBACard';
    default:
      return 'Card';
  }
};

export const convertCardHitToCard = (
  hit: CardHit
):
  | {
      __typename: 'Card';
      assetId: string;
      slug: string;
      sport: Sport;
      foilUrl: string | null;
      foilColorUrl: string | null;
      rarityTyped: Rarity;
      borderRadius: number;
      displayRarity: string;
      pictureUrl: string;
      seasonYear: number;
      singleCivilYear: boolean;
      serialNumber: number;
      supply: number;
      anyPositions: Position[];
      anyTeam: {
        __typename: 'Club';
        slug: string;
        name: string;
      };
      anyPlayer: {
        __typename: 'Player';
        slug: string;
        displayName: string;
      };
      user: {
        __typename: 'User';
        slug: string;
      } | null;
      tokenOwner: {
        __typename: 'TokenOwner';
        id: string;
        user: {
          __typename: 'User';
          slug: string;
        };
      } | null;
      sportSeason: {
        __typename: 'SportSeason';
        name: string;
      };
      webmVideoUrl: string | null;
      webmLowResVideoUrl: string | null;
      movVideoUrl: string | null;
      movLowResVideoUrl: string | null;
      sealed: boolean;
    }
  | {
      __typename: 'BaseballCard';
      slug: string;
      baseballPositions: BaseballPosition[];
      assetId: string;
      sport: Sport;
      foilUrl: string | null;
      foilColorUrl: string | null;
      rarityTyped: Rarity;
      displayRarity: string;
      borderRadius: number;
      pictureUrl: string;
      seasonYear: number;
      singleCivilYear: boolean;
      serialNumber: number;
      supply: number;
      anyPositions: Position[];
      anyTeam: {
        __typename: 'Club';
        slug: string;
        name: string;
      };
      anyPlayer: {
        __typename: 'Player';
        slug: string;
        displayName: string;
      };
      user: {
        __typename: 'User';
        slug: string;
      } | null;
      tokenOwner: {
        __typename: 'TokenOwner';
        id: string;
        user: {
          __typename: 'User';
          slug: string;
        };
      } | null;
      sportSeason: {
        __typename: 'SportSeason';
        name: string;
      };
      webmVideoUrl: string | null;
      webmLowResVideoUrl: string | null;
      movVideoUrl: string | null;
      movLowResVideoUrl: string | null;
      sealed: boolean;
    }
  | {
      __typename: 'NBACard';
      assetId: string;
      slug: string;
      nbaPositions: NBAPosition[];
      sport: Sport;
      foilUrl: string | null;
      foilColorUrl: string | null;
      rarityTyped: Rarity;
      displayRarity: string;
      borderRadius: number;
      pictureUrl: string;
      seasonYear: number;
      singleCivilYear: boolean;
      serialNumber: number;
      supply: number;
      anyPositions: Position[];
      anyTeam: {
        __typename: 'Club';
        slug: string;
        name: string;
      };
      anyPlayer: {
        __typename: 'Player';
        slug: string;
        displayName: string;
      };
      user: {
        __typename: 'User';
        slug: string;
      } | null;
      tokenOwner: {
        __typename: 'TokenOwner';
        id: string;
        user: {
          __typename: 'User';
          slug: string;
        };
      } | null;
      sportSeason: {
        __typename: 'SportSeason';
        name: string;
      };
      webmVideoUrl: string | null;
      webmLowResVideoUrl: string | null;
      movVideoUrl: string | null;
      movLowResVideoUrl: string | null;
      sealed: boolean;
    } => ({
  __typename: convertCardHitToTypename(hit) as any,
  assetId: hit.asset_id,
  baseballPositions: [],
  nbaPositions: [],
  sealed: false,
  foilUrl: null,
  foilColorUrl: null,
  ...convertCardHitToToken(hit),
  webmVideoUrl: hit.video_urls?.webm || null,
  webmLowResVideoUrl: hit.video_urls?.webm || null,
  movVideoUrl: hit.video_urls?.mov || null,
  movLowResVideoUrl: hit.video_urls?.mov || null,
});

export const tokenHitFragment = gql`
  fragment Algolia_CardHit_anyCard on AnyCardInterface {
    assetId
    slug
    sport
    rarityTyped
    serialNumber
    seasonYear
    anyPositions
    anyTeam {
      slug
      name
    }
    anyPlayer {
      slug
      displayName
    }
    pictureUrl
    user {
      slug
    }
    tradeableStatus
    walletStatus
  }
` as TypedDocumentNode<Algolia_CardHit_anyCard>;

const tradeableWalletStatus = ['internal', 'mapped'];

export const sportFilter = (sport: Sport): string =>
  `sport:${sport.toLowerCase()}`;

/**
 * Escapes a user-controlled value for safe interpolation inside a
 * double-quoted Algolia filter literal — e.g. `name:"${toFilterLiteral(x)}"`.
 *
 * Backslashes must be escaped first to avoid double-escaping the quotes
 * we add immediately after.
 */
export const toFilterLiteral = (value: string): string =>
  value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

const sanitizeFilters = (filters: (string | null | false | undefined)[]) =>
  filters.filter(Boolean).filter(string => string !== '');

export const joinFiltersWithAnd = (
  filters: (string | null | false | undefined)[]
) => sanitizeFilters(filters).join(' AND ');

export const joinFiltersWithOr = (
  filters: (string | null | false | undefined)[]
) => (filters.length ? `(${sanitizeFilters(filters).join(' OR ')})` : '');

export const parseFilters = (filters: string | undefined) => {
  const inlineFilters = filters?.replace(/\s+/g, ' ').trim();
  if (!inlineFilters) return [];

  const tokens: string[] = [];
  let currentToken = '';
  let parentCount = 0;
  let inQuotes = false;

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < inlineFilters.length; i++) {
    const char = inlineFilters[i];

    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === '(' && !inQuotes) {
      parentCount += 1;
      if (parentCount === 1) {
        if (currentToken.trim()) {
          tokens.push(currentToken.trim());
        }
        currentToken = '(';
        // eslint-disable-next-line no-continue
        continue;
      }
    } else if (char === ')' && !inQuotes) {
      parentCount -= 1;
      if (parentCount === 0) {
        currentToken += char;
        tokens.push(currentToken.replace(/\s+/g, ' ').trim());
        currentToken = '';
        // eslint-disable-next-line no-continue
        continue;
      }
    }

    if (parentCount === 0 && inlineFilters.slice(i, i + 3) === 'AND') {
      if (currentToken.trim()) {
        tokens.push(currentToken.trim());
      }
      currentToken = '';
      i += 2;
      // eslint-disable-next-line no-continue
      continue;
    }

    currentToken += char;
  }

  if (currentToken.trim()) {
    tokens.push(currentToken.trim());
  }

  return tokens;
};

export const removeFilter = (
  filters: string | undefined,
  filterToRemove: string
) => {
  if (filters === undefined) return undefined;
  return joinFiltersWithAnd(
    parseFilters(filters).filter(f => f !== filterToRemove)
  );
};

export function mergeResults<T extends { slug: string }>(
  hits: T[],
  saved: T[]
) {
  const ids = hits.map(o => o.slug);
  return saved.reduce((all, selectedCard) => {
    if (ids.includes(selectedCard.slug)) {
      return all;
    }
    return [selectedCard, ...all];
  }, hits || []);
}

export function getAlgoliaHosts(algoliaApplicationId: string) {
  // APPID-dsn.algolia.net: the Algolia read replica that is the closest to the end-user's IP address (geoip)
  //
  // We build the algolia hosts to target following this strategy (the client will try one after the other):
  //  - first & main host: DSN (closest) hostname for geo-optimized reads
  //  - next 3 hosts (DNS provider = NS1):
  //     the 3 primary machines on the `algolia.net` domain
  //  - next 3 hosts (DNS provider = Cloudflare, used when NS1 is down):
  //     the 3 primary machines on the `algolianet.com` domain
  const firstHost = `${algoliaApplicationId}-dsn.algolia.net`;
  const algoliaHosts = [firstHost]
    .concat(
      shuffle([
        `${algoliaApplicationId}-1.algolia.net`,
        `${algoliaApplicationId}-2.algolia.net`,
        `${algoliaApplicationId}-3.algolia.net`,
      ])
    )
    .concat(
      shuffle([
        `${algoliaApplicationId}-1.algolianet.com`,
        `${algoliaApplicationId}-2.algolianet.com`,
        `${algoliaApplicationId}-3.algolianet.com`,
      ])
    );

  return algoliaHosts;
}

export const getUserCardFilters = (userId: string | undefined): string => {
  const filtersArray = [
    `tradeable_status:yes`,
    `(${tradeableWalletStatus.map(r => `wallet_status:${r}`).join(' OR ')})`,
  ];
  if (userId) filtersArray.push(`user.id:${userId}`);
  return joinFiltersWithAnd(filtersArray);
};

type CardSortConfiguration = {
  indexPrefix?: 'Cards On Sale';
  withBestValue?: boolean;
  withHighestAverageScore?: true;
  withPopularPlayer?: true;
  withHighestPrice?: boolean;
  withEndingSoon?: boolean;
  withLowestPrice?: boolean;
  withNew?: boolean;
};

export type CardPageSortName = Extract<AlgoliaCardIndexesName, GallerySortName>;

export function createCardSorts(
  config?: Omit<CardSortConfiguration, 'indexPrefix'>
): CardPageSortName[];
export function createCardSorts(
  config: CardSortConfiguration
): AlgoliaCardIndexesNames;
export function createCardSorts({
  indexPrefix,
  withBestValue,
  withHighestAverageScore,
  withPopularPlayer,
  withHighestPrice = true,
  withEndingSoon = true,
  withLowestPrice = true,
  withNew = true,
}: CardSortConfiguration = {}): AlgoliaCardIndexesNames {
  const result = (
    [
      withNew && 'Newly Listed',
      withBestValue && 'Best Value',
      withPopularPlayer && 'Popular Player',
      withEndingSoon && 'Ending Soon',
      withHighestPrice && 'Highest Price',
      withLowestPrice && 'Lowest Price',
      withHighestAverageScore && 'Highest Average Score',
    ] as const
  ).filter(Boolean);

  return result.map(sort =>
    indexPrefix
      ? (`${indexPrefix} ${sort}` as `${typeof indexPrefix} ${typeof sort}`)
      : sort
  );
}

export interface StackProps {
  algoliaDistinctKey: string;
  params?: SearchParameters;
  count: number;
}
